'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import { type HotTakesSlideDirection, resolveHotTakesSlideDirection } from './hot-takes-slide-motion';

/** Hot Takes carousel auto-advance interval. */
export const HOT_TAKES_AUTO_ADVANCE_MS = 8_000 as const;

/** Biggest prize pools leaderboard spotlight cadence (per outcome). */
export const PRIZE_POOLS_SPOTLIGHT_MS = 4_500 as const;
export type { HotTakesSlideDirection };
export interface UseHotTakesCarouselOptions {
  /** When false, slides change only via `goTo` (chevrons). Defaults to true. */
  autoAdvance?: boolean;
  /** Auto-advance interval in ms. Defaults to {@link HOT_TAKES_AUTO_ADVANCE_MS}. */
  autoAdvanceMs?: number;
}
export interface UseHotTakesCarouselReturn {
  activeIndex: number;
  direction: HotTakesSlideDirection;
  goTo: (index: number) => void;
  pause: () => void;
  resume: () => void;
}
interface CarouselPosition {
  activeIndex: number;
  direction: HotTakesSlideDirection;
}

/**
 * Cycles through up to three hot-thesis cards every {@link HOT_TAKES_AUTO_ADVANCE_MS}.
 * Pauses while hovered/focused; respects reduced motion.
 */
export function useHotTakesCarousel(slideCount, t0) {
  const $ = _c(19);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    autoAdvance: t2,
    autoAdvanceMs: t3
  } = t1;
  const autoAdvance = t2 === undefined ? true : t2;
  const autoAdvanceMs = t3 === undefined ? HOT_TAKES_AUTO_ADVANCE_MS : t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      activeIndex: 0,
      direction: 1
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [position, setPosition] = useState(t4);
  const [pauseCount, setPauseCount] = useState(0);
  const prefersReducedMotion = useReducedMotion() === true;
  const activeIndex = slideCount > 0 && position.activeIndex < slideCount ? position.activeIndex : 0;
  let t5;
  if ($[3] !== activeIndex || $[4] !== slideCount) {
    t5 = index => {
      if (slideCount <= 0) {
        return;
      }
      const wrapped = (index % slideCount + slideCount) % slideCount;
      setPosition({
        activeIndex: wrapped,
        direction: resolveHotTakesSlideDirection(activeIndex, wrapped)
      });
    };
    $[3] = activeIndex;
    $[4] = slideCount;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const goTo = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setPauseCount(_temp);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const pause = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setPauseCount(_temp2);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const resume = t7;
  const isPaused = pauseCount > 0;
  let t8;
  let t9;
  if ($[8] !== autoAdvance || $[9] !== autoAdvanceMs || $[10] !== isPaused || $[11] !== prefersReducedMotion || $[12] !== slideCount) {
    t8 = () => {
      if (!autoAdvance) {
        return;
      }
      if (isPaused) {
        return;
      }
      if (prefersReducedMotion) {
        return;
      }
      if (slideCount <= 1) {
        return;
      }
      const handle = setInterval(() => {
        setPosition(current => ({
          activeIndex: ((current.activeIndex < slideCount ? current.activeIndex : 0) + 1) % slideCount,
          direction: 1
        }));
      }, autoAdvanceMs);
      return () => {
        clearInterval(handle);
      };
    };
    t9 = [autoAdvance, autoAdvanceMs, isPaused, slideCount, prefersReducedMotion];
    $[8] = autoAdvance;
    $[9] = autoAdvanceMs;
    $[10] = isPaused;
    $[11] = prefersReducedMotion;
    $[12] = slideCount;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  if ($[15] !== activeIndex || $[16] !== goTo || $[17] !== position.direction) {
    t10 = {
      activeIndex,
      direction: position.direction,
      goTo,
      pause,
      resume
    };
    $[15] = activeIndex;
    $[16] = goTo;
    $[17] = position.direction;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function _temp2(count_0) {
  return count_0 > 0 ? count_0 - 1 : 0;
}
function _temp(count) {
  return count + 1;
}